import { useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useRetryableQuery } from './useRetryableQuery';

export interface PlanLimitsConfig {
  plan_free_tickets: number;
  plan_free_agents: number;
  plan_captain_tickets: number;
  plan_captain_agents: number;
  plan_wonder_tickets: number;
  plan_wonder_agents: number;
  plan_super_tickets: number;
  plan_super_agents: number;
}

export interface WorkspaceUsage {
  tickets_used: number;
  tickets_limit: number;
  agents_count: number;
  agents_limit: number;
  pending_invitations: number;
}

// Default config values
const DEFAULT_CONFIG: PlanLimitsConfig = {
  plan_free_tickets: 10,
  plan_free_agents: 1,
  plan_captain_tickets: 300,
  plan_captain_agents: 3,
  plan_wonder_tickets: 1000,
  plan_wonder_agents: 10,
  plan_super_tickets: 5000,
  plan_super_agents: 20,
};

export function usePlanLimitsConfig() {
  const queryFn = useCallback(async (): Promise<PlanLimitsConfig> => {
    const { data, error } = await supabase
      .from('plan_limits_config')
      .select('*')
      .limit(1)
      .maybeSingle();

    if (error) throw error;

    if (data) {
      return {
        plan_free_tickets: data.plan_free_tickets,
        plan_free_agents: data.plan_free_agents,
        plan_captain_tickets: data.plan_captain_tickets,
        plan_captain_agents: data.plan_captain_agents,
        plan_wonder_tickets: data.plan_wonder_tickets,
        plan_wonder_agents: data.plan_wonder_agents,
        plan_super_tickets: data.plan_super_tickets,
        plan_super_agents: data.plan_super_agents,
      };
    }

    return DEFAULT_CONFIG;
  }, []);

  const { data, loading } = useRetryableQuery({
    queryFn,
    maxRetries: 2,
    showToastOnError: false,
    errorMessage: 'Erreur lors du chargement des limites',
  });

  const config = useMemo(() => data ?? DEFAULT_CONFIG, [data]);

  // Get limits for a specific plan
  const getPlanLimits = useCallback((planId: string) => {
    switch (planId) {
      case 'super':
        return { tickets: config.plan_super_tickets, agents: config.plan_super_agents };
      case 'wonder':
        return { tickets: config.plan_wonder_tickets, agents: config.plan_wonder_agents };
      case 'captain':
        return { tickets: config.plan_captain_tickets, agents: config.plan_captain_agents };
      default:
        return { tickets: config.plan_free_tickets, agents: config.plan_free_agents };
    }
  }, [config]);

  return { config, loading, getPlanLimits };
}

export function useWorkspaceUsage(workspaceId: string | undefined) {
  const queryFn = useCallback(async (): Promise<WorkspaceUsage | null> => {
    if (!workspaceId) return null;

    const { data, error } = await supabase
      .rpc('get_workspace_usage', { _workspace_id: workspaceId });

    if (error) throw error;

    if (data && typeof data === 'object' && !Array.isArray(data)) {
      const usageData = data as Record<string, unknown>;
      return {
        tickets_used: Number(usageData.tickets_used) || 0,
        tickets_limit: Number(usageData.tickets_limit) || 0,
        agents_count: Number(usageData.agents_count) || 0,
        agents_limit: Number(usageData.agents_limit) || 0,
        pending_invitations: Number(usageData.pending_invitations) || 0,
      };
    }

    return null;
  }, [workspaceId]);

  const { data: usage, loading, retry } = useRetryableQuery({
    queryFn,
    enabled: !!workspaceId,
    maxRetries: 2,
    showToastOnError: false,
    errorMessage: 'Erreur lors du chargement de l\'utilisation',
  });

  const canCreateTicket = usage ? usage.tickets_used < usage.tickets_limit : true;
  const canAddAgent = usage ? usage.agents_count < usage.agents_limit : true;
  
  const ticketsRemaining = usage ? usage.tickets_limit - usage.tickets_used : 0;
  const agentsRemaining = usage ? usage.agents_limit - usage.agents_count : 0;

  return { 
    usage, 
    loading, 
    refetch: retry,
    canCreateTicket,
    canAddAgent,
    ticketsRemaining,
    agentsRemaining,
  };
}
